import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import PDFThumbnail from '../components/PDFThumbnail';
import PDFViewer from '../components/PDFViewer';
import { 
  CalendarIcon,
  ArrowRightIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, orderBy('created_time', 'desc'));
      const snapshot = await getDocs(q);
      const articlesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setArticles(articlesData);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const categories = ['all', ...new Set(articles.map(a => a.category).filter(Boolean))];
  
  const filteredArticles = filterCategory === 'all' 
    ? articles 
    : articles.filter(a => a.category === filterCategory);

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-secondary-900 to-primary-900">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <DocumentTextIcon className="w-16 h-16 text-oldham-gold mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Dauntless Articles
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Inspiring stories, training insights, and community updates from Oldham Athletics
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-gray-800 border-b border-gray-700 sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setFilterCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterCategory === category
                    ? 'bg-oldham-gold text-gray-900'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
          <div className="text-center mt-3">
            <p className="text-gray-400 text-sm">
              Showing {filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'}
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-oldham-gold"></div>
            </div>
          ) : filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <div key={article.id} className="group">
                  {article.pdf_url ? (
                    <div onClick={() => setSelectedPdf(article.pdf_url)} className="cursor-pointer">
                      <PDFThumbnail
                        pdfUrl={article.pdf_url}
                        title={article.title}
                        description={article.excerpt || article.content?.substring(0, 150) + '...'}
                        onClick={() => setSelectedPdf(article.pdf_url)}
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-oldham-gold transition-all duration-300 hover:transform hover:-translate-y-2 h-full flex flex-col">
                      {article.image_url && (
                        <img 
                          src={article.image_url} 
                          alt={article.title}
                          className="w-full h-48 object-cover"
                        />
                      )}
                      <div className="p-6 flex-grow flex flex-col">
                        <div className="flex items-center text-sm text-gray-400 mb-3">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formatDate(article.created_time)}
                        </div>
                        
                        {article.category && (
                          <span className="inline-block px-3 py-1 bg-oldham-gold/20 text-oldham-gold rounded-full text-xs font-semibold mb-3 self-start">
                            {article.category}
                          </span>
                        )}
                        
                        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-oldham-gold transition-colors">
                          {article.title}
                        </h3>
                        
                        <p className="text-gray-400 mb-4 leading-relaxed flex-grow">
                          {article.excerpt || article.content?.substring(0, 150) + '...'}
                        </p>
                        
                        {article.author && (
                          <p className="text-sm text-gray-500 mb-4">
                            By {article.author}
                          </p>
                        )}
                        
                        <Link
                          to={`/articles/${article.id}`}
                          className="text-oldham-gold hover:text-oldham-gold/80 font-semibold inline-flex items-center group-hover:translate-x-1 transition-all mt-auto"
                        >
                          Read More
                          <ArrowRightIcon className="ml-2 h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <DocumentTextIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-gray-400 mb-2">
                No articles found
              </h3>
              <p className="text-gray-500">
                {filterCategory !== 'all' 
                  ? `No articles in the "${filterCategory}" category yet.`
                  : 'Check back soon for inspiring stories and updates!'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* PDF Viewer Modal */}
      {selectedPdf && (
        <PDFViewer
          pdfUrl={selectedPdf}
          onClose={() => setSelectedPdf(null)}
        />
      )}

      {/* Back to Home */}
      <section className="py-12 bg-gray-800 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-oldham-gold hover:text-oldham-gold/80 font-semibold text-lg"
          >
            <ArrowRightIcon className="mr-2 h-5 w-5 rotate-180" />
            Back to Home
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Articles;

